import { GeotabApiError } from './geotab-client';

// ── Range-windowed Geotab fetching ─────────────────────────────────────────
// Geotab's `Get` has no pagination: a call with `resultsLimit: N` silently truncates at N.
// For high-volume types (ExceptionEvent, Trip, LogRecord) over a long date range, that drops
// data and skews any analysis built on it. These helpers recover everything by splitting a
// window in half whenever it tops out at the cap, and bound concurrency so a long range
// doesn't fire hundreds of calls at once and trip Geotab's rate limiter.

export const MAX_CONCURRENT = 8;            // simultaneous API calls ceiling
export const MIN_CHUNK_MS   = 60 * 60 * 1000; // never sub-split a window below 1 hour

// A fleet-wide (unfiltered by device) Trip.Get silently caps at 25,000 rows regardless of the
// resultsLimit requested — confirmed by requesting 50,000 against a large production database
// and getting back exactly 25,000 for three different window lengths. Passing that higher number
// as perCallLimit means the `rows.length >= perCallLimit` truncation check never fires (25,000 <
// 50,000), so an over-cap window is silently accepted as complete instead of being split further.
// Every caller that fetches Trip fleet-wide (no deviceSearch) must use this as perCallLimit.
export const FLEET_WIDE_TRIP_LIMIT = 25000;

/** Split [from, to] into N-day windows (inclusive of a final short remainder). */
export function splitDateRange(
  fromISO: string, toISO: string, daysPerChunk: number,
): Array<[string, string]> {
  const chunks: Array<[string, string]> = [];
  const start   = new Date(fromISO).getTime();
  const end     = new Date(toISO).getTime();
  const chunkMs = daysPerChunk * 86_400_000;
  for (let t = start; t < end; t += chunkMs) {
    const chunkEnd = Math.min(t + chunkMs, end);
    chunks.push([new Date(t).toISOString(), new Date(chunkEnd).toISOString()]);
  }
  return chunks.length ? chunks : [[fromISO, toISO]];
}

/** Run `fn` over `items` with at most `limit` calls in flight (order-preserving results). */
export async function mapLimit<T, R>(
  items: T[], limit: number, fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

function dedupById<T extends { id?: string }>(rows: T[]): T[] {
  const seen = new Set<string>();
  return rows.filter(r => {
    const id = r.id;
    if (!id) return true;            // no id (e.g. LogRecord) → keep; duplicates are harmless
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

interface FetchRangeOpts<T> {
  fromISO: string;
  toISO: string;
  perCallLimit: number;
  /** Fetch one window. Return the rows, or null to skip this window (e.g. on a caught error). */
  fetch: (fromISO: string, toISO: string) => Promise<T[] | null>;
  initialChunkDays?: number;   // pre-split before adaptive splitting (default 7; large = 1 window)
  concurrency?: number;        // cap on the initial windows fetched at once (default MAX_CONCURRENT)
  minChunkMs?: number;         // floor for adaptive splitting (default MIN_CHUNK_MS)
  onCap?: () => void;          // invoked if even a min-size window is still at the cap
}

/**
 * Fetch one [fromISO, toISO] window, recovering past the per-call cap by recursively splitting
 * the window in half (down to `minChunkMs`) whenever it tops out. Throwing inside `fetch`
 * propagates; returning null skips that window. Does NOT dedup — callers that flatten many
 * windows themselves should dedup by id (boundaries are shared).
 */
export async function fetchAdaptiveWindow<T>(
  fromISO: string,
  toISO: string,
  perCallLimit: number,
  fetch: (fromISO: string, toISO: string) => Promise<T[] | null>,
  opts: { minChunkMs?: number; onCap?: () => void } = {},
): Promise<T[]> {
  const minChunkMs = opts.minChunkMs ?? MIN_CHUNK_MS;
  const rows = await fetch(fromISO, toISO);
  if (rows === null) return [];
  if (rows.length >= perCallLimit) {
    const fMs = new Date(fromISO).getTime();
    const tMs = new Date(toISO).getTime();
    if (tMs - fMs > minChunkMs) {
      const mid = new Date(fMs + Math.floor((tMs - fMs) / 2)).toISOString();
      const [a, b] = await Promise.all([
        fetchAdaptiveWindow(fromISO, mid, perCallLimit, fetch, opts),
        fetchAdaptiveWindow(mid, toISO, perCallLimit, fetch, opts),
      ]);
      return a.concat(b);
    }
    opts.onCap?.();
  }
  return rows;
}

// ── Retry with backoff ────────────────────────────────────────────────────
// The Geotab client throws GeotabApiError with the real exception name
// (OverLimitException, etc.) preserved — see geotab-client.ts. Nothing in the
// app previously backed off on rate limiting, which under sustained load
// (e.g. one LogRecord call per device across an 800+ vehicle fleet) reads as
// scattered, silent data loss rather than a rate-limit event: a handful of
// calls fail, a two-attempt/400ms retry doesn't clear a ~1-minute rate-limit
// window, and the caller is left concluding "some devices have no data" with
// no signal that the real cause was throughput, not the vehicle.
//
// The schedule below is deliberately patient — this trades wall-clock time
// for completeness, which is the right trade for an accuracy-first report the
// user is willing to wait on, not a live interaction.
const RETRY_DELAYS_MS = [1000, 3000, 8000, 20000, 45000];

const sleep = (ms: number): Promise<void> => new Promise(r => setTimeout(r, ms));

/**
 * Retry `fn` on failure with a progressively longer backoff (see
 * RETRY_DELAYS_MS), long enough on the later attempts to plausibly cross a
 * Geotab rate-limit window. When the thrown error carries a `retryAfterMs`
 * hint (standalone mode only — see GeotabApiError), that takes precedence
 * over the schedule since it's the server's own answer, not a guess.
 * Rejects with the last error once the schedule is exhausted.
 */
export async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= RETRY_DELAYS_MS.length) throw err;
      const hinted = err instanceof GeotabApiError ? err.retryAfterMs : undefined;
      const scheduled = RETRY_DELAYS_MS[attempt];
      const delay = hinted && hinted > scheduled ? hinted : scheduled;
      // Jitter avoids every one of the 8 concurrent workers retrying in lockstep
      // and re-tripping the same limiter the instant it resets.
      await sleep(delay * (0.85 + Math.random() * 0.3));
    }
  }
}

/**
 * Fetch every row of a type across [fromISO, toISO]: pre-split into initial windows, adaptively
 * sub-split any that top out, run with bounded concurrency, and de-duplicate by id. Best for a
 * single stream (one rule, or fleet-wide). Throwing inside `fetch` propagates; null skips.
 */
export async function fetchRangeChunked<T extends { id?: string }>(
  opts: FetchRangeOpts<T>,
): Promise<T[]> {
  const { fromISO, toISO, perCallLimit, fetch } = opts;
  const initialChunkDays = opts.initialChunkDays ?? 7;
  const concurrency      = opts.concurrency ?? MAX_CONCURRENT;
  const minChunkMs       = opts.minChunkMs ?? MIN_CHUNK_MS;

  const windows = splitDateRange(fromISO, toISO, initialChunkDays);
  const lists   = await mapLimit(windows, concurrency,
    ([f, t]) => fetchAdaptiveWindow(f, t, perCallLimit, fetch, { minChunkMs, onCap: opts.onCap }));
  return dedupById(lists.flat());
}
